import React, { FC } from 'react';
import { TContentHubHeroBannerProps } from './types/productHeroBanner.types';
import { ComponentRendering, isEditorActive, RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { ProductHeroBannerModel } from './models/productHeroBanner.model';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { targetLinkdsk } from '../../utils/targetDskLink';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import FadeDownEffect from '../@shared/fadeDownEffect';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { googleClickCtaEvent } from '../../utils/analytics';
import useGetBuildAndPriceLink from '../../hooks/useGetBuildAndPriceLink';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';
import DictionaryTerm from '../@shared/dictionaryTerm';

const ProductHeroBanner: FC<TContentHubHeroBannerProps> = (props) => {
  const model = new ProductHeroBannerModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const apiResponse = isEmptyResponse(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const buildAndPriceUrl = useGetBuildAndPriceLink('line');

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent
      rendering={props?.rendering as ComponentRendering}
      title={model?.title ?? model?.eyebrow ?? ''}
      template="Hero Banner Series Listing"
    >
      <div
        className={`hero-banner product-hero-banner ${!model?.isGradientDisabled ? 'hero-banner--background' : ''}`}
        data-text-color={model.textColor?.toLowerCase()}
      >
        {/* Need to update useHeroBanner hook if the images sent to Picture component  are changed */}
        <Picture
          productImageProps={{
            desktopRendition: model.desktopImage,
            desktopSDRendition: model.desktopSDImage,
            tabletRendition: model.tabletImage,
            tabletLandscapeRendition: model.tabletLandscapeImage,
            mobileRendition: model.mobileImage,
            imageClassName: 'hero-banner__image hero-banner__image--desktop hero-banner__image--strict-height',
            prioritize: true,
            pictureClassName: 'hero-banner__image-wrapper',
          }}
        />
        <div className="hero-banner__content hero-banner__content--align-top hero-banner__content--top-margin">
          <FadeDownEffect delay={1000}>
            <Text
              tag="p"
              field={{ value: model.eyebrow }}
              className={`hero-banner__eyebrow hero-banner--${model.textColor}`}
              editable={false}
            />
          </FadeDownEffect>
          <FadeDownEffect delay={2000}>
            <Text
              tag="h1"
              field={{ value: model.title }}
              className={`hero-banner__title hero-banner--${model.textColor}`}
              editable={false}
            />
          </FadeDownEffect>
          <FadeDownEffect delay={3000}>
            <RichText
              tag="div"
              field={{ value: model.description }}
              className={`hero-banner__description hero-banner--${model.textColor} rich-text`}
              editable={false}
            />
          </FadeDownEffect>
          {
            model?.enableBuildPriceCTA && buildAndPriceUrl && (
              <span className={'hero-banner__link hero-banner__link--primary'}>
                <FadeDownEffect delay={4000}>
                  <CTAButton type={ctaEnums.primaryRed}>
                    <RoutableNotEditableLink
                      href={appendRootToInternalLink(buildAndPriceUrl)}
                    >
                      <DictionaryTerm keyword={'cnhi-build-price'} />
                    </RoutableNotEditableLink>
                  </CTAButton>
                </FadeDownEffect>
              </span>
            )
          }
          {model?.primaryLink?.label && model?.primaryLink?.url && (
            <span className={'hero-banner__link hero-banner__link--primary'}>
              <FadeDownEffect delay={4000}>
                <CTAButton type={ctaEnums.primaryRed}>
                  <RoutableNotEditableLink
                    href={model.primaryLinkUrl}
                    target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, isDesktop)}
                    onClick={(): void => googleClickCtaEvent('Hero', model?.primaryLink?.label, model?.title ?? model?.eyebrow)}
                  >
                    {model.primaryLink.label}
                  </RoutableNotEditableLink>
                </CTAButton>
              </FadeDownEffect>
            </span>
          )}
          {model?.secondaryLink?.label && model?.secondaryLink?.url && (
            <span className={'hero-banner__link hero-banner__link--secondary'}>
              <FadeDownEffect delay={4000}>
                <CTAButton type={ctaEnums.primaryWhite}>
                  <RoutableNotEditableLink
                    href={model.secondaryLinkUrl}
                    target={targetLinkdsk({ value: { href: model?.secondaryLink?.url, target: model?.secondaryLink?.target } }, isDesktop)}
                    onClick={(): void => googleClickCtaEvent('Hero', model?.secondaryLink?.label, model?.title ?? model?.eyebrow)}
                  >
                    {model.secondaryLink.label}
                  </RoutableNotEditableLink>
                </CTAButton>
              </FadeDownEffect>
            </span>
          )}
        </div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default ProductHeroBanner;
